import { ChangeEvent, FC } from 'react';

import { LEAD_MERGE_TAG_CLASS, splitLeadMergeTags } from '../../utils/leadsAdmin';

type LeadMergeTagTextProps = {
  value?: string | null;
  fallback?: string;
  className?: string;
};

/** Renders plain text with {{merge_tag}} tokens as Courier New chips. */
export const LeadMergeTagText: FC<LeadMergeTagTextProps> = ({
  value,
  fallback = '',
  className = '',
}) => {
  const raw = (value ?? '').trim() ? value ?? '' : fallback;
  const segments = splitLeadMergeTags(raw);
  return (
    <span className={className}>
      {segments.map((segment, index) =>
        segment.isMergeTag ? (
          <code key={`${segment.text}-${index}`} className={LEAD_MERGE_TAG_CLASS}>
            {segment.text}
          </code>
        ) : (
          <span key={`${segment.text}-${index}`}>{segment.text}</span>
        )
      )}
    </span>
  );
};

type LeadMergeTagSubjectInputProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  className?: string;
  inputClassName?: string;
};

/**
 * Subject field that chips {{merge_tag}} tokens while typing.
 * The native input stays transparent so caret/selection still work.
 */
export const LeadMergeTagSubjectInput: FC<LeadMergeTagSubjectInputProps> = ({
  value,
  onChange,
  placeholder = '',
  required = false,
  className = '',
  inputClassName = '',
}) => {
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value);
  };

  return (
    <div className={`relative ${className}`}>
      <div
        aria-hidden="true"
        className={`pointer-events-none absolute inset-0 flex items-center overflow-hidden whitespace-pre px-3 py-2 font-open-sans text-sm ${
          value ? 'text-[#2C3E50]' : 'text-transparent'
        }`}
      >
        {value ? <LeadMergeTagText value={value} /> : null}
      </div>
      <input
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        required={required}
        className={`relative w-full bg-transparent caret-primary-0 font-open-sans text-sm ${
          value ? 'text-transparent' : 'text-[#2C3E50]'
        } ${inputClassName}`}
      />
    </div>
  );
};
